import { Box, Paper, Slider, styled, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useUserSettingsContext } from "../../contexts/Staffing/UserSettingsContext";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import { NEUTRAL } from "../../theme/tokens";
import type { BaseNurseShift, Nurse, NurseFullRole, OpenShiftRequest, ReplaceShiftRequest, ShiftStaffTimes } from "../../utils/models";
import { ShiftRow } from "./ShiftRow";
import { useTimeline } from "./useTimeline";

const TimelineHeader = styled(Box)(({ theme }) => ({
	display: "flex",
	position: "relative",
	height: 20,
	borderBottom: `1px solid ${theme.palette.divider}`,
	backgroundColor: theme.palette.background.paper,
}));

interface TimeLabel {
	time: number;
	label: string;
}

const TimeLabel = styled(Typography)(({ theme }) => ({
	position: "absolute",
	transform: "translateX(-50%)",
	fontSize: 12,
	fontWeight: 500,
	color: theme.palette.text.secondary,
	top: "50%",
	marginTop: -8,
}));

interface IProps {
	date: DateTime;
	nurses: BaseNurseShift[];
	requests: (OpenShiftRequest | ReplaceShiftRequest)[];
	shiftStart: DateTime;
	shiftEnd: DateTime;
	startTime: DateTime;
	setStartTime: (shiftStart: DateTime) => void;
	endTime: DateTime;
	setEndTime: (shiftEnd: DateTime) => void;
	onShiftClick: (shiftTimes: ShiftStaffTimes) => void;
	selectedRoles: (NurseFullRole | "All")[];
	selectedNurse: Nurse | null;
}

export const DayViewTimeline: FC<IProps> = ({
	date,
	nurses,
	requests,
	shiftStart,
	shiftEnd,
	startTime,
	setStartTime,
	endTime,
	setEndTime,
	onShiftClick,
	selectedRoles,
	selectedNurse,
}) => {
	const startHoursDiffExact: number = useMemo(() => Math.ceil(startTime.diff(shiftStart).as("hours")), [date, shiftStart, startTime]);

	const endHoursDiffExact: number = useMemo(() => Math.ceil(shiftEnd.diff(endTime).as("hours")), [date, endTime, shiftEnd]);

	const hoursDiffExact: number = Math.max(24, Math.abs(shiftStart.diff(shiftEnd).as("hours")));

	const [timeRange, setTimeRange] = useState<number[]>([startHoursDiffExact, hoursDiffExact - endHoursDiffExact]);

	const { settings } = useUserSettingsContext();
	const formatPersonName = useFormatPersonName();
	const { timelineRows } = useTimeline({
		nurses,
		requests,
		useRoleGroups: settings.groupRoles,
		// Group the timeline bars by job (falls back to role for job-less shifts).
		groupingMode: "job",
	});

	useEffect(() => {
		setTimeRange([startHoursDiffExact, hoursDiffExact - endHoursDiffExact]);
	}, [startHoursDiffExact, endHoursDiffExact, date]);

	const timeLabels: TimeLabel[] = useMemo(() => {
		const labels: TimeLabel[] = [];
		const hourStep = Math.max(1, Math.ceil(hoursDiffExact / 6));

		for (let h = 0; h <= hoursDiffExact; h += hourStep) {
			const time = shiftStart.plus({ hours: h });
			const formattedTime = time.toFormat("hha");

			labels.push({
				time: h,
				label: formattedTime,
			});
		}

		return labels;
	}, [hoursDiffExact, shiftStart]);

	const handleTimeRangeChange = useCallback(
		(_: Event, newValue: number | number[]): void => {
			const newTimeRange = newValue as [number, number];
			setTimeRange(newTimeRange);

			setStartTime(shiftStart.plus({ hours: newTimeRange[0] }));
			setEndTime(shiftEnd.minus({ hours: hoursDiffExact - newTimeRange[1] }));
		},
		[shiftStart, shiftEnd, setStartTime, setEndTime, hoursDiffExact],
	);

	const formatTimeLabel = (value: number): string => {
		const time = shiftStart.plus({ hours: value });
		return time.toFormat("hA");
	};

	return (
		<Paper>
			<Box sx={{ px: 3 }}>
				<TimelineHeader>
					{timeLabels.map((timeLabel) => (
						<TimeLabel key={timeLabel.time} variant="caption" sx={{ left: `${(timeLabel.time / hoursDiffExact) * 100}%` }}>
							{timeLabel.label}
						</TimeLabel>
					))}
				</TimelineHeader>
				<Slider
					value={timeRange}
					onChange={handleTimeRangeChange}
					min={0}
					max={hoursDiffExact}
					step={1}
					marks
					valueLabelDisplay="auto"
					valueLabelFormat={formatTimeLabel}
					disableSwap
					sx={{
						p: 0,
						"& .MuiSlider-thumb": {
							height: 20,
							width: 6,
							borderRadius: 0,
							"&:before": {
								display: "none",
							},
							"&:after": {
								display: "none",
							},
						},
						"& .MuiSlider-track": {
							height: 2,
							border: "none",
						},
						"& .MuiSlider-rail": {
							height: 2,
							opacity: 0.5,
							backgroundColor: NEUTRAL[400],
						},
					}}
				/>
			</Box>
			<Box>
				{Object.keys(timelineRows)
					.sort((keyA: string, keyB: string) => {
						const shiftsA: ShiftStaffTimes[] = Object.keys(timelineRows[keyA])
							.flatMap((val: any) => timelineRows[keyA][val])
							.map((a) => a.shift);

						const shiftsB: ShiftStaffTimes[] = Object.keys(timelineRows[keyB])
							.flatMap((val: any) => timelineRows[keyB][val])
							.map((a) => a.shift);

						const totalCountA = shiftsA.reduce((sum, shift) => sum + (shift.count || 0) + (shift.requestedCount || 0), 0);

						const totalCountB = shiftsB.reduce((sum, shift) => sum + (shift.count || 0) + (shift.requestedCount || 0), 0);

						return totalCountB - totalCountA;
					})
					?.map((key: string) => {
						const shiftStaffTimes = Object.keys(timelineRows[key])
							.flatMap((val: any) => timelineRows[key][val])
							.map((a) => a.shift);
						return (
							<Box key={key} sx={{ px: 3 }}>
								<ShiftRow
									startTime={startTime}
									endTime={endTime}
									shiftStaffTimes={shiftStaffTimes}
									shiftStart={shiftStart}
									hoursRange={hoursDiffExact}
									onShiftClick={onShiftClick}
									selectedRoles={selectedRoles}
									searchText={selectedNurse ? formatPersonName(selectedNurse) : null}
								/>
							</Box>
						);
					})}
			</Box>
		</Paper>
	);
};
